/**
 * SupplierDraftModal — generates a bilingual (EN + ZH) supplier chase email
 * for missing compliance documentation.
 *
 * Triggered from:
 * - Checklist item rows (status = unchecked or fail)
 * - Outstanding items widget
 */
import { useState, useRef } from 'react';
import { authFetch } from '../api';

export interface SupplierDraftModalProps {
  /** Initial product name (from checklist title) */
  initialProductName?: string;
  /** Pre-seeded missing items (from checklist item text) */
  initialMissingItems?: string[];
  /** Checklist item ID for context */
  checklistItemId?: string;
  onClose: () => void;
}

type DraftTab = 'en' | 'zh' | 'combined';

interface DraftResult {
  draft_en: string;
  draft_zh: string;
  combined: string;
}

const COUNTRY_OPTIONS = [
  { code: 'CN', label: 'China' },
  { code: 'TW', label: 'Taiwan' },
  { code: 'VN', label: 'Vietnam' },
  { code: 'IN', label: 'India' },
  { code: 'TH', label: 'Thailand' },
  { code: 'IT', label: 'Italy' },
  { code: 'DE', label: 'Germany' },
  { code: 'ES', label: 'Spain' },
  { code: 'US', label: 'United States' },
  { code: 'AU', label: 'Australia' },
  { code: 'OTHER', label: 'Other' },
];

export default function SupplierDraftModal({
  initialProductName = '',
  initialMissingItems = [''],
  checklistItemId,
  onClose,
}: SupplierDraftModalProps) {
  // Form fields
  const [productName, setProductName] = useState(initialProductName);
  const [certType, setCertType] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [supplierCountry, setSupplierCountry] = useState('CN');
  const [missingItems, setMissingItems] = useState<string[]>(
    initialMissingItems.length > 0 ? initialMissingItems : ['']
  );

  // State
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftResult | null>(null);
  const [activeTab, setActiveTab] = useState<DraftTab>('en');
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Missing items helpers
  const updateItem = (idx: number, val: string) => {
    setMissingItems((prev) => prev.map((v, i) => (i === idx ? val : v)));
  };
  const addItem = () => setMissingItems((prev) => [...prev, '']);
  const removeItem = (idx: number) =>
    setMissingItems((prev) => prev.filter((_, i) => i !== idx));

  const handleGenerate = async () => {
    const trimmedItems = missingItems.map((s) => s.trim()).filter(Boolean);
    if (!productName.trim()) { setError('Product name is required.'); return; }
    if (!certType.trim()) { setError('Cert / compliance type is required.'); return; }
    if (!supplierName.trim()) { setError('Supplier name is required.'); return; }
    if (trimmedItems.length === 0) { setError('At least one missing item is required.'); return; }

    setError(null);
    setGenerating(true);

    try {
      const res = await authFetch('/api/supplier-draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_name: productName.trim(),
          cert_type: certType.trim(),
          supplier_name: supplierName.trim(),
          supplier_country: supplierCountry,
          missing_items: trimmedItems,
          checklist_item_id: checklistItemId,
        }),
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(errBody.error ?? `HTTP ${res.status}`);
      }

      const data = (await res.json()) as DraftResult;
      setDraft(data);
      setActiveTab('en');
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : 'Failed to generate draft. Please try again.'
      );
    } finally {
      setGenerating(false);
    }
  };

  const currentDraftText =
    draft
      ? activeTab === 'en'
        ? draft.draft_en
        : activeTab === 'zh'
          ? draft.draft_zh
          : draft.combined
      : '';

  const handleCopy = async () => {
    if (!currentDraftText) return;
    try {
      await navigator.clipboard.writeText(currentDraftText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      if (textareaRef.current) {
        textareaRef.current.select();
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const tabLabel: Record<DraftTab, string> = {
    en: '🇦🇺 English',
    zh: '🇨🇳 Chinese',
    combined: '📄 Combined',
  };

  return (
    /* Overlay */
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '16px',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Modal box */}
      <div
        style={{
          background: 'var(--surface-1, #fff)',
          borderRadius: 12,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          width: '100%',
          maxWidth: 640,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid var(--border, #e2e8f0)',
          }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: '16px', color: 'var(--ink-1, #1e293b)' }}>
              📧 Draft Supplier Email
            </div>
            <div style={{ fontSize: '12px', color: 'var(--ink-3, #94a3b8)', marginTop: 2 }}>
              Bilingual English + Chinese supplier chase email
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--ink-3, #94a3b8)',
              fontSize: '18px',
              padding: '4px',
            }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {!draft ? (
            /* ─── Form ─── */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {error && (
                <div
                  style={{
                    padding: '8px 12px',
                    borderRadius: 6,
                    background: '#fee2e2',
                    color: '#dc2626',
                    fontSize: '13px',
                  }}
                >
                  {error}
                </div>
              )}

              <Field label="Product name *">
                <input
                  type="text"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder="e.g. Elysian Basin Mixer EBM-200"
                  style={inputStyle}
                />
              </Field>

              <Field label="Certification / compliance type *">
                <input
                  type="text"
                  value={certType}
                  onChange={(e) => setCertType(e.target.value)}
                  placeholder="e.g. WaterMark certification, WELS registration"
                  style={inputStyle}
                />
              </Field>

              <Field label="Supplier name *">
                <input
                  type="text"
                  value={supplierName}
                  onChange={(e) => setSupplierName(e.target.value)}
                  placeholder="e.g. Guangzhou Precision Fittings Co."
                  style={inputStyle}
                />
              </Field>

              <Field label="Supplier country">
                <select
                  value={supplierCountry}
                  onChange={(e) => setSupplierCountry(e.target.value)}
                  style={inputStyle}
                >
                  {COUNTRY_OPTIONS.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label} ({c.code})
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Missing documents *">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {missingItems.map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: 6 }}>
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => updateItem(idx, e.target.value)}
                        placeholder={`Document ${idx + 1}, e.g. WaterMark licence copy`}
                        style={{ ...inputStyle, flex: 1 }}
                      />
                      {missingItems.length > 1 && (
                        <button
                          onClick={() => removeItem(idx)}
                          style={{
                            background: 'none',
                            border: '1px solid var(--border, #e2e8f0)',
                            borderRadius: 6,
                            cursor: 'pointer',
                            padding: '0 10px',
                            color: 'var(--ink-3, #94a3b8)',
                            fontSize: '14px',
                          }}
                          aria-label="Remove item"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  {missingItems.length < 10 && (
                    <button
                      onClick={addItem}
                      style={{
                        alignSelf: 'flex-start',
                        background: 'none',
                        border: '1px dashed var(--border, #e2e8f0)',
                        borderRadius: 6,
                        cursor: 'pointer',
                        padding: '5px 12px',
                        color: 'var(--ink-3, #94a3b8)',
                        fontSize: '12px',
                      }}
                    >
                      + Add another document
                    </button>
                  )}
                </div>
              </Field>
            </div>
          ) : (
            /* ─── Draft view ─── */
            <div>
              {/* Tab switcher */}
              <div
                style={{
                  display: 'flex',
                  gap: 4,
                  marginBottom: 12,
                  background: 'var(--surface-2, #f1f5f9)',
                  borderRadius: 8,
                  padding: 4,
                }}
              >
                {(['en', 'zh', 'combined'] as DraftTab[]).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    style={{
                      flex: 1,
                      padding: '6px 0',
                      borderRadius: 6,
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: activeTab === tab ? 600 : 400,
                      background:
                        activeTab === tab
                          ? 'var(--surface-1, #fff)'
                          : 'transparent',
                      color:
                        activeTab === tab
                          ? 'var(--ink-1, #1e293b)'
                          : 'var(--ink-3, #94a3b8)',
                      boxShadow:
                        activeTab === tab
                          ? '0 1px 3px rgba(0,0,0,0.08)'
                          : 'none',
                      transition: 'all 0.1s',
                    }}
                  >
                    {tabLabel[tab]}
                  </button>
                ))}
              </div>

              {/* Draft textarea */}
              <textarea
                ref={textareaRef}
                value={currentDraftText}
                onChange={(e) => {
                  const val = e.target.value;
                  setDraft((prev) => {
                    if (!prev) return prev;
                    if (activeTab === 'en') return { ...prev, draft_en: val };
                    if (activeTab === 'zh') return { ...prev, draft_zh: val };
                    return { ...prev, combined: val };
                  });
                }}
                style={{
                  width: '100%',
                  minHeight: 300,
                  padding: '12px',
                  border: '1px solid var(--border, #e2e8f0)',
                  borderRadius: 8,
                  fontSize: '13px',
                  lineHeight: 1.6,
                  fontFamily: 'inherit',
                  color: 'var(--ink-1, #1e293b)',
                  background: 'var(--surface-1, #fff)',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
              <div
                style={{
                  fontSize: '11px',
                  color: 'var(--ink-3, #94a3b8)',
                  marginTop: 4,
                }}
              >
                You can edit the draft above before copying.
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '14px 20px',
            borderTop: '1px solid var(--border, #e2e8f0)',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', gap: 8 }}>
            {draft && (
              <button
                onClick={() => {
                  setDraft(null);
                  setError(null);
                }}
                style={secondaryBtnStyle}
              >
                ↩ Regenerate
              </button>
            )}
            <button onClick={onClose} style={secondaryBtnStyle}>
              Cancel
            </button>
          </div>

          <div>
            {!draft ? (
              <button
                onClick={() => void handleGenerate()}
                disabled={generating}
                style={{
                  ...primaryBtnStyle,
                  opacity: generating ? 0.7 : 1,
                  cursor: generating ? 'not-allowed' : 'pointer',
                  minWidth: 160,
                }}
              >
                {generating ? '⏳ Drafting…' : '✨ Generate draft'}
              </button>
            ) : (
              <button
                onClick={() => void handleCopy()}
                style={primaryBtnStyle}
              >
                {copied ? '✅ Copied!' : '📋 Copy to clipboard'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Sub-components ─── */

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        style={{
          display: 'block',
          fontSize: '12px',
          fontWeight: 500,
          color: 'var(--ink-2, #475569)',
          marginBottom: 5,
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

/* ─── Shared styles ─── */

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--border, #e2e8f0)',
  borderRadius: 6,
  fontSize: '13px',
  color: 'var(--ink-1, #1e293b)',
  background: 'var(--surface-1, #fff)',
  boxSizing: 'border-box',
  outline: 'none',
};

const primaryBtnStyle: React.CSSProperties = {
  padding: '8px 18px',
  borderRadius: 7,
  border: 'none',
  background: 'var(--accent, #2563eb)',
  color: '#fff',
  fontWeight: 600,
  fontSize: '13px',
  cursor: 'pointer',
};

const secondaryBtnStyle: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 7,
  border: '1px solid var(--border, #e2e8f0)',
  background: 'var(--surface-1, #fff)',
  color: 'var(--ink-2, #475569)',
  fontSize: '13px',
  cursor: 'pointer',
};
